import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { getCalendar, getNotices, getNoticesById, postCalendarRsvp } from '@/api/common';
import { Banner, Empty, Modal, QueryState, Spinner } from './ui';
import { asList, errMsg, fmtDateTime, unwrap } from './data';

interface NoticeRow { id: number; title: string; excerpt?: string | null; pinned?: boolean; published_at?: string | null; read?: boolean }
interface NoticeDetail { id: number; title: string; body: string; published_at?: string | null }
interface EventRow { id: number; title: string; description: string | null; type: string; starts_at: string; ends_at: string | null; all_day: boolean; location: string | null; rsvp: 'yes' | 'no' | 'maybe' | null; rsvp_open: boolean }

export function NoticesPanel() {
  const [openId, setOpenId] = useState<number | null>(null);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['notices'], queryFn: () => getNotices().then((r) => asList<NoticeRow>(unwrap(r))) });
  const d = useQuery({
    queryKey: ['notice', openId],
    enabled: openId !== null,
    queryFn: () => getNoticesById(openId as number).then((r) => unwrap<NoticeDetail>(r)),
  });
  return (
    <>
      <QueryState q={q} isEmpty={!q.data?.length} empty="No notices.">
        <ul className="divide-y">
          {q.data?.map((n) => (
            <li key={n.id}>
              <button
                className="w-full text-left py-3 flex justify-between gap-3"
                onClick={() => { setOpenId(n.id); qc.invalidateQueries({ queryKey: ['notices'] }); }}
              >
                <span>
                  <span className={n.read ? '' : 'font-semibold'}>{n.pinned ? '[Pinned] ' : ''}{n.title}</span>
                  {n.excerpt && <span className="block text-sm text-slate-500">{n.excerpt}</span>}
                </span>
                <span className="text-xs text-slate-400 shrink-0">{fmtDateTime(n.published_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      </QueryState>
      {openId !== null && (
        <Modal title={d.data?.title ?? 'Notice'} onClose={() => setOpenId(null)} wide>
          {d.isLoading ? <Spinner /> : d.isError ? <Banner tone="error">{errMsg(d.error)}</Banner> : (
            <div>
              <div className="text-xs text-slate-400 mb-2">{fmtDateTime(d.data?.published_at)}</div>
              <div className="whitespace-pre-wrap text-sm">{d.data?.body}</div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

const monthRange = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { from: `${y}-${pad(mo)}-01`, to: `${y}-${pad(mo)}-${pad(new Date(y, mo, 0).getDate())}` };
};

export function EventsPanel() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [err, setErr] = useState('');
  const range = monthRange(month);
  const q = useQuery({ queryKey: ['events', month], queryFn: () => getCalendar(range).then((r) => asList<EventRow>(unwrap(r))) });
  const rsvp = useMutation({
    mutationFn: (v: { id: number; response: 'yes' | 'no' | 'maybe' }) => postCalendarRsvp(v.id, { response: v.response }),
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['events'] }); },
    onError: (e) => setErr(errMsg(e)),
  });
  const shift = (d: number) => {
    const [y, m] = month.split('-').map(Number);
    const dt = new Date(y, m - 1 + d, 1);
    setMonth(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
  };
  return (
    <div className="space-y-3">
      {err && <Banner tone="error">{err}</Banner>}
      <Card
        title={new Date(`${month}-01`).toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })}
        action={<div className="flex gap-2"><Button variant="ghost" onClick={() => shift(-1)}>Previous</Button><Button variant="ghost" onClick={() => shift(1)}>Next</Button></div>}
      >
        <QueryState q={q} isEmpty={!q.data?.length} empty="No events this month.">
          <ul className="divide-y">
            {q.data?.map((e) => (
              <li key={e.id} className="py-3 flex justify-between gap-4">
                <div>
                  <div className="font-medium">{e.title} <span className="text-xs text-slate-500">({e.type})</span></div>
                  <div className="text-sm text-slate-500">{e.all_day ? new Date(e.starts_at).toLocaleDateString('en-NG') : fmtDateTime(e.starts_at)}{e.location ? ` - ${e.location}` : ''}</div>
                  {e.description && <div className="text-sm mt-1">{e.description}</div>}
                </div>
                {e.rsvp_open && (
                  <div className="flex gap-1 items-start shrink-0">
                    {(['yes', 'maybe', 'no'] as const).map((r) => (
                      <Button key={r} variant={e.rsvp === r ? 'primary' : 'ghost'} disabled={rsvp.isPending} onClick={() => rsvp.mutate({ id: e.id, response: r })}>
                        {r === 'yes' ? 'Going' : r === 'no' ? "Can't go" : 'Maybe'}
                      </Button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

export { Empty };
